/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { TsriServiceEndpointService } from "@tivocorp/data-domain-lib";
import type { TServicesParams } from "@tivocorp/data-domain-lib";

import { CommonHeaders } from "../CommonHeaders";
import { DeviceModel } from "../device";
import { ApplicationFeatureArea } from "../enums";
import { ErrorTypes, PartialResponseError } from "../error";
import { Locale, LocaleKeys } from "../locale";
import { Log } from "../logging/Log";
import { RetryMechanism } from "../retrymechanism";
import { RETRY_POLICY } from "../types";
import { CRITICAL_TSRI_SERVICES, TSRI_BASE_URL, TSRI_ID } from "../../config";
import type { ITsriEndpointRecoveryListener } from "../interfaces/ITsriEndpointRecoveryListener";

/**
 * Single point of TSRI endpoint lookup, with recovery for a service that is
 * absent from the cache.
 *
 * ## Why this exists
 *
 * `appStartup/fetchGlobalEndpoints` validates the startup response against
 * `REQUIRED_PRE_SIGN_IN_SERVICES` only — six of the ~40 names in
 * `TIVO_SERVICES`. Every other service passes validation whether or not TSRI
 * actually returned it, and the gap only surfaces much later, at the moment
 * some feature needs the endpoint. Before this class, that moment threw a bare
 * `Error` from the domain lib: no overlay, no retry, and for the ten static
 * call sites not even a re-fetch.
 *
 * Detection is therefore **lazy** — at lookup, not at startup — because that is
 * the first point at which the app knows the endpoint is actually wanted.
 *
 * ## What recovery means
 *
 * TSRI is one call returning the whole catalog; there is no per-endpoint fetch.
 * "Retrying the endpoint" can only mean re-calling
 * `GET /tsriEndpoints/{tsriId}` and re-checking whether the name has appeared.
 * If the backend has genuinely not registered the service, that never converges
 * — which is why the budget is INTERACTIVE (~35s over 4 attempts) rather than
 * CRITICAL's unbounded loop. Exhaustion is a real outcome here, not a fallback.
 *
 * Recovery only runs while the catalog has **not** yet loaded (a cold start
 * whose initial `fetchGlobalEndpoints` has not populated the cache). Once the
 * catalog has loaded successfully, a per-service miss is served as genuinely
 * absent without re-fetching: re-calling the single all-or-nothing catalog
 * cannot add a name it already omitted, so a second call would only duplicate
 * the request. A failed *initial* fetch still retries through its own
 * RetryMechanism at the startup layer.
 *
 * ## Two entry points
 *
 * - {@link resolve} — async. Awaits recovery, then returns the URL or throws.
 * - {@link resolveOrSchedule} — sync, for URL builders on render paths that
 *   cannot become async without turning their callers async up into the views.
 *   Returns `null` on a miss and kicks the same recovery off in the background,
 *   so a later render picks up the endpoint once it lands.
 *
 * Concurrent misses share one in-flight catalog fetch (see
 * {@link refreshCatalog}) and one recovery per service (see
 * `mPendingRecovery`), so a post-sign-in burst of misses produces a single
 * network call per round rather than one per caller.
 */
export class TsriEndpointResolver {
  private static readonly TAG: string = "TsriEndpointResolver";

  private static mInstance: TsriEndpointResolver | null = null;

  private readonly mListeners: Set<ITsriEndpointRecoveryListener> = new Set();

  /**
   * The catalog fetch currently in flight, shared by every caller that misses
   * while it runs. Cleared in `finally` so the next miss starts a fresh one.
   */
  private mInFlightRefresh: Promise<void> | null = null;

  /**
   * Recovery in progress per service name. A second `resolve()` for a service
   * already recovering joins the first rather than starting its own retry loop,
   * so listeners see one started/settled pair per service.
   */
  private readonly mPendingRecovery: Map<string, Promise<string>> = new Map();

  public static getInstance(): TsriEndpointResolver {
    if (TsriEndpointResolver.mInstance === null) {
      TsriEndpointResolver.mInstance = new TsriEndpointResolver();
    }
    return TsriEndpointResolver.mInstance;
  }

  /**
   * Whether a missing endpoint for this service should block the app behind
   * the alert overlay, as opposed to degrading the feature silently.
   */
  public static isCriticalService(serviceName: string): boolean {
    return CRITICAL_TSRI_SERVICES.includes(serviceName);
  }

  // ──────────────────────────────────────────────────────────────────
  // Listener registration
  // ──────────────────────────────────────────────────────────────────

  public addRecoveryListener(listener: ITsriEndpointRecoveryListener): void {
    this.mListeners.add(listener);
  }

  public removeRecoveryListener(listener: ITsriEndpointRecoveryListener): void {
    this.mListeners.delete(listener);
  }

  // ──────────────────────────────────────────────────────────────────
  // Lookup
  // ──────────────────────────────────────────────────────────────────

  /**
   * Reads the endpoint cache without triggering recovery.
   *
   * @param serviceName - TSRI catalog name
   * @returns The base URL, or `null` when the service is not cached
   */
  public peek(serviceName: string): string | null {
    try {
      const url: string =
        TsriServiceEndpointService.getServiceEndpointByServiceName(serviceName);
      // Normalised to `null` so every caller has one shape to test for. An
      // empty string is as unusable as a missing entry and must not be handed
      // out as a basePath.
      return url || null;
    } catch {
      // The domain lib signals "not cached" by throwing. That is the normal
      // miss path here, not an error worth logging twice — the caller logs it
      // with the context of what it was about to do.
      return null;
    }
  }

  /**
   * Resolves an endpoint, recovering from a cache miss.
   *
   * On a miss: notifies listeners (critical services only), then re-fetches the
   * catalog on the INTERACTIVE policy until the service appears. On success the
   * URL is returned and listeners are told to dismiss; on exhaustion listeners
   * are told recovery failed and the error is rethrown so the calling feature
   * fails rather than proceeding with an empty basePath.
   *
   * The re-fetch runs only while the catalog has not yet loaded. Once startup
   * has loaded it, a miss rejects immediately without a second network call —
   * re-fetching the single all-or-nothing catalog cannot add an absent name.
   *
   * @param serviceName - TSRI catalog name
   * @returns The base URL
   * @throws PartialResponseError - Service still absent after the retry budget
   */
  public async resolve(serviceName: string): Promise<string> {
    const cached: string | null = this.peek(serviceName);
    if (cached !== null) {
      return cached;
    }

    // The catalog is a single all-or-nothing document, fetched once at startup
    // by `appStartup/fetchGlobalEndpoints`. Once it has loaded successfully,
    // re-calling `GET /tsriEndpoints/{tsriId}` cannot surface a name it did not
    // already contain — it only issues a duplicate network request. So a miss
    // against an already-loaded catalog is served as genuinely absent, without
    // an independent re-fetch. The recovery re-fetch below is reserved for the
    // cold-start case where the initial fetch has not yet populated the cache;
    // a failed *initial* fetch keeps retrying through its own RetryMechanism.
    if (TsriServiceEndpointService.hasEndpoints()) {
      return this.rejectMissingFromLoadedCatalog(serviceName);
    }

    const pending: Promise<string> | undefined =
      this.mPendingRecovery.get(serviceName);
    if (pending !== undefined) {
      Log.d(
        TsriEndpointResolver.TAG,
        `resolve() joining recovery already in progress for ${serviceName}`
      );
      return pending;
    }

    const recovery: Promise<string> = this.recover(serviceName).finally(() => {
      this.mPendingRecovery.delete(serviceName);
    });
    this.mPendingRecovery.set(serviceName, recovery);
    return recovery;
  }

  /**
   * Rejects a lookup for a service absent from an already-loaded catalog,
   * without a network re-fetch.
   *
   * Reached only after a successful catalog load: every critical service is
   * validated present at startup (`REQUIRED_PRE_SIGN_IN_SERVICES`), so in
   * practice this path is exercised by non-critical services, which degrade
   * silently exactly as an exhausted recovery would.
   */
  private rejectMissingFromLoadedCatalog(serviceName: string): Promise<string> {
    Log.w(
      TsriEndpointResolver.TAG,
      `resolve() ${serviceName} absent from the already-loaded TSRI catalog ` +
        `— serving as unavailable without re-fetching`
    );
    return Promise.reject(
      new PartialResponseError(
        ErrorTypes.TSRI_PARTIAL_RESPONSE_A015,
        `Service ${serviceName} absent from TsriServiceEndpointGet response`,
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        ),
        Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_TITLE)
      )
    );
  }

  /**
   * Synchronous counterpart to {@link resolve}, for URL builders called from
   * render paths.
   *
   * A miss returns `null` and starts recovery in the background; it never
   * blocks and never throws. The caller degrades — an unresolved image host
   * means an empty logo slot, not a failed screen — and the next render after
   * recovery lands gets the real URL.
   *
   * @param serviceName - TSRI catalog name
   * @returns The base URL, or `null` while the endpoint is unavailable
   */
  public resolveOrSchedule(serviceName: string): string | null {
    const cached: string | null = this.peek(serviceName);
    if (cached !== null) {
      return cached;
    }

    Log.w(
      TsriEndpointResolver.TAG,
      `resolveOrSchedule() ${serviceName} unavailable — recovering in background`
    );
    // Deliberately not awaited: the caller is synchronous. `resolve()` already
    // dedupes per service, so repeated renders during recovery add no calls.
    this.resolve(serviceName).catch((error: unknown) => {
      Log.e(
        TsriEndpointResolver.TAG,
        `resolveOrSchedule() background recovery failed for ${serviceName}`,
        error
      );
    });
    return null;
  }

  // ──────────────────────────────────────────────────────────────────
  // Recovery
  // ──────────────────────────────────────────────────────────────────

  /**
   * Runs the bounded re-fetch loop for one service and notifies listeners
   * around it.
   */
  private async recover(serviceName: string): Promise<string> {
    const isCritical: boolean =
      TsriEndpointResolver.isCriticalService(serviceName);

    Log.w(
      TsriEndpointResolver.TAG,
      `recover() ${serviceName} missing from endpoint cache ` +
        `(critical=${String(isCritical)}) — re-fetching TSRI catalog`
    );

    if (isCritical) {
      this.notifyRecoveryStarted(serviceName);
    }

    try {
      const endpoint: string = await RetryMechanism.callServiceWithRetry(
        async (): Promise<string> => {
          await this.refreshCatalog();
          const refreshed: string | null = this.peek(serviceName);
          if (refreshed === null) {
            // The call succeeded but the catalog still omits the service, so
            // there is no HTTP status to key off. RetryMechanism treats a
            // statusless error as no-connectivity and retries it, which is the
            // behaviour wanted: try again in case registration is propagating.
            throw new PartialResponseError(
              ErrorTypes.TSRI_PARTIAL_RESPONSE_A015,
              `Service ${serviceName} absent from TsriServiceEndpointGet response`,
              Locale.getString(
                LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
              ),
              Locale.getString(
                LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_TITLE
              )
            );
          }
          return refreshed;
        },
        [],
        {
          retryPolicy: RETRY_POLICY.INTERACTIVE,
          httpMethod: "GET"
        }
      );

      Log.i(
        TsriEndpointResolver.TAG,
        `recover() ${serviceName} recovered from TSRI`
      );
      if (isCritical) {
        this.notifyRecovered(serviceName);
      }
      return endpoint;
    } catch (error: unknown) {
      Log.e(
        TsriEndpointResolver.TAG,
        `recover() ${serviceName} still unavailable after retry budget`,
        error
      );
      if (isCritical) {
        this.notifyRecoveryFailed(serviceName, error);
      }
      throw error;
    }
  }

  /**
   * Re-fetches the whole TSRI catalog, coalescing concurrent callers onto one
   * request.
   *
   * The response is merged into `TsriServiceEndpointService`'s static cache by
   * the service itself, so there is nothing to return — callers re-`peek()`
   * afterwards for the name they care about.
   */
  private async refreshCatalog(): Promise<void> {
    const inFlight: Promise<void> | null = this.mInFlightRefresh;
    if (inFlight !== null) {
      Log.d(
        TsriEndpointResolver.TAG,
        "refreshCatalog() joining in-flight catalog fetch"
      );
      return inFlight;
    }

    const refresh: Promise<void> = this.fetchCatalog().finally(() => {
      this.mInFlightRefresh = null;
    });
    this.mInFlightRefresh = refresh;
    return refresh;
  }

  private async fetchCatalog(): Promise<void> {
    const originRequestId: string = DeviceModel.getDeviceUuid() || "";
    const common: CommonHeaders = new CommonHeaders(
      ApplicationFeatureArea.LOGIN
    );
    // No bodyId: TSRI is not body-scoped, so one param shape serves both the
    // pre- and post-sign-in callers.
    const params: TServicesParams = common.toServicesParams({
      basePath: TSRI_BASE_URL,
      xAmznRequestId: originRequestId
    });

    const service: TsriServiceEndpointService = new TsriServiceEndpointService(
      params
    );
    await service.fetchServiceEndpoints({
      tsriId: TSRI_ID,
      originRequestId: originRequestId
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // Notification
  // ──────────────────────────────────────────────────────────────────

  private notifyRecoveryStarted(serviceName: string): void {
    for (const listener of Array.from(this.mListeners)) {
      try {
        listener.onEndpointRecoveryStarted(serviceName);
      } catch (error: unknown) {
        Log.e(TsriEndpointResolver.TAG, "notifyRecoveryStarted()", error);
      }
    }
  }

  private notifyRecovered(serviceName: string): void {
    for (const listener of Array.from(this.mListeners)) {
      try {
        listener.onEndpointRecovered(serviceName);
      } catch (error: unknown) {
        Log.e(TsriEndpointResolver.TAG, "notifyRecovered()", error);
      }
    }
  }

  private notifyRecoveryFailed(serviceName: string, failure: unknown): void {
    for (const listener of Array.from(this.mListeners)) {
      try {
        listener.onEndpointRecoveryFailed(serviceName, failure);
      } catch (error: unknown) {
        Log.e(TsriEndpointResolver.TAG, "notifyRecoveryFailed()", error);
      }
    }
  }
}
